import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, combineLatest, filter, map, take } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Membership Guard
 *
 * Redirects users to /account-not-supported if they have no organization memberships
 * and no Admin/SuperAdmin global role.
 *
 * Users with any memberships or Admin/SuperAdmin roles are allowed through.
 * Users with no claims at all are allowed through (token may not have claims set yet).
 */
@Injectable({
  providedIn: 'root'
})
export class MembershipGuard {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    return combineLatest([
      this.authService.loading$,
      this.authService.currentUser$
    ]).pipe(
      filter(([loading]) => !loading),
      take(1),
      map(([_, user]) => {
        if (!user) {
          return this.router.createUrlTree(['/login']);
        }

        const claims = user.customClaims;

        // No claims parsed yet — let them through (token may not have claims set)
        if (!claims) {
          return true;
        }

        // Has any organization membership — they're a pilot/owner/etc.
        if (claims.memberships.length > 0) {
          return true;
        }

        // Has Admin or SuperAdmin — always allowed
        if (claims.isAdmin()) {
          return true;
        }

        // No memberships and no admin role — redirect to account-not-supported
        // If globalClaims is 0 and memberships is empty, the token may just not have claims yet
        if (claims.globalClaims === 0) {
          return true;
        }

        return this.router.createUrlTree(['/account-not-supported']);
      })
    );
  }

  canActivateChild(): Observable<boolean | UrlTree> {
    return this.canActivate();
  }
}
